// ModuleCard — one card per module in the Modules tab.
// Switch is optimistic: state flips immediately, reverts on API error.
// Locked modules show LockIcon + disabled switch.
// Turning OFF a module requires AlertDialog confirmation.

import { useState } from "react";
import type { JSX } from "react";
import { Lock } from "lucide-react";
import { toast } from "sonner";
import type { ModuleKey, ModuleState } from "../lib/adminApiClient.js";
import { toggleModule } from "../lib/adminApiClient.js";
import { MODULE_META } from "../lib/modulesMeta.js";

interface Props {
  tenantId: string;
  moduleKey: ModuleKey;
  state: ModuleState;
  onChanged: (key: ModuleKey, active: boolean) => void;
}

export function ModuleCard({ tenantId, moduleKey, state, onChanged }: Props): JSX.Element {
  const meta = MODULE_META[moduleKey];
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);

  async function commit(active: boolean): Promise<void> {
    setBusy(true);
    onChanged(moduleKey, active);
    try {
      await toggleModule(tenantId, moduleKey, active);
      toast.success(`${meta.label} ${active ? "activated" : "deactivated"}.`);
    } catch {
      onChanged(moduleKey, !active); // revert
      toast.error(`Failed to ${active ? "activate" : "deactivate"} ${meta.label}.`);
    } finally {
      setBusy(false);
    }
  }

  function handleToggle(checked: boolean): void {
    if (state.locked) return;
    if (!checked) { setConfirming(true); return; }
    void commit(true);
  }

  return (
    <>
      <div className={`rounded-xl border p-4 space-y-2 transition-colors ${
        state.active ? "bg-white border-slate-200" : "bg-slate-50 border-slate-200 opacity-70"
      }`}>
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5">
              <span className="text-sm font-semibold text-slate-800 truncate">{meta.label}</span>
              {state.locked && (
                <span title="Locked — cannot be changed"><Lock className="h-3.5 w-3.5 text-slate-400 shrink-0" /></span>
              )}
            </div>
            <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">{meta.description}</p>
          </div>
          <button
            role="switch"
            aria-checked={state.active}
            aria-label={`Toggle ${meta.label}`}
            disabled={state.locked || busy}
            onClick={() => handleToggle(!state.active)}
            className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-50 ${
              state.active ? "bg-indigo-600" : "bg-slate-300"
            }`}
          >
            <span className={`pointer-events-none inline-block h-4 w-4 rounded-full bg-white shadow transition-transform ${
              state.active ? "translate-x-4" : "translate-x-0"
            }`} />
          </button>
        </div>
      </div>

      {confirming && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-xl max-w-sm w-full p-6 space-y-4">
            <h3 className="text-base font-semibold text-slate-900">Deactivate {meta.label}?</h3>
            <p className="text-sm text-slate-500">
              Deactivating this module will hide it from all users of this tenant immediately.
            </p>
            <div className="flex justify-end gap-2">
              <button onClick={() => setConfirming(false)} className={BTN_GHOST}>Cancel</button>
              <button onClick={() => { setConfirming(false); void commit(false); }} className={BTN_DANGER}>
                Deactivate
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

const BTN_GHOST = "px-3 py-1.5 text-sm rounded-lg bg-slate-100 text-slate-600 hover:bg-slate-200";
const BTN_DANGER = "px-3 py-1.5 text-sm rounded-lg bg-red-600 text-white hover:bg-red-700";
